import { uiSurface } from "shared/ui/uiSurface"
import { Box, Divider, Stack, Typography } from "@mui/material"
import dayjs from "dayjs"
import { FC, forwardRef, useImperativeHandle, useRef } from "react"
import { useTranslation } from "react-i18next"
import { DashboardOrder, OrderDetail } from "../../apis/appApi"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Unified label data — works for both DashboardOrder (batch) and OrderDetail (single) */
export type LabelData = {
  reference: string
  deliveryDate: string
  desiredArrivalTime?: string
  notes?: string | null
  amount?: number | null
  client?: {
    firstName: string
    lastName: string
    organisation?: string | null
  }
  address?: {
    streetNumber?: string
    streetName?: string
    appartNumber?: string | null
    city?: string
    state?: string
    postcode?: string
    notes?: string | null
    label?: string | null
  }
  tags?: Array<{
    tag?: { label: string; color?: string } | null
    tagId?: number
  }>
}

/** Convert a DashboardOrder to LabelData */
export function dashboardOrderToLabel(order: DashboardOrder): LabelData {
  return {
    reference: order.reference,
    deliveryDate: order.deliveryDate,
    desiredArrivalTime: order.desiredArrivalTime,
    notes: order.notes,
    amount: order.amount,
    client: order.client
      ? {
          firstName: order.client.firstName,
          lastName: order.client.lastName,
        }
      : undefined,
    address: order.address
      ? {
          streetNumber: order.address.streetNumber,
          streetName: order.address.streetName,
          appartNumber: order.address.appartNumber,
          city: order.address.city,
          postcode: order.address.postcode,
        }
      : undefined,
    tags: order.tags?.map((t) => ({
      tag: t.tag ? { label: t.tag.label, color: t.tag.color } : null,
      tagId: t.tagId,
    })),
  }
}

/** Convert an OrderDetail to LabelData */
export function orderDetailToLabel(order: OrderDetail): LabelData {
  return {
    reference: order.reference,
    deliveryDate: order.deliveryDate,
    desiredArrivalTime: order.desiredArrivalTime,
    notes: order.notes,
    amount: order.amount,
    client: order.client
      ? {
          firstName: order.client.firstName,
          lastName: order.client.lastName,
          organisation: order.client.organisation,
        }
      : undefined,
    address: order.address
      ? {
          streetNumber: order.address.streetNumber,
          streetName: order.address.streetName,
          appartNumber: order.address.appartNumber,
          city: order.address.city,
          state: order.address.state,
          postcode: order.address.postcode,
          notes: order.address.notes,
          label: order.address.label,
        }
      : undefined,
    tags: order.tags?.map((t) => ({
      tag: t.tag ? { label: t.tag.label, color: t.tag.color } : null,
      tagId: t.tagId,
    })),
  }
}

// ---------------------------------------------------------------------------
// Single Label
// ---------------------------------------------------------------------------

const SingleLabel: FC<{ label: LabelData; t: (key: string) => string }> = ({
  label,
  t,
}) => {
  const formattedDate = dayjs(label.deliveryDate).isValid()
    ? dayjs(label.deliveryDate).format("ddd, MMM D, YYYY")
    : label.deliveryDate

  const addressLine1 = [label.address?.streetNumber, label.address?.streetName]
    .filter(Boolean)
    .join(" ")
  const addressLine1WithApt = label.address?.appartNumber
    ? `${addressLine1}, apt. ${label.address.appartNumber}`
    : addressLine1
  const addressLine2 = [
    label.address?.city,
    label.address?.state,
    label.address?.postcode,
  ]
    .filter(Boolean)
    .join(", ")

  const clientName = label.client
    ? `${label.client.firstName} ${label.client.lastName}`
    : ""

  return (
    <Box
      className="print-label"
      sx={{
        border: "2px solid #000",
        borderRadius: 1,
        p: 2.5,
        mb: 2,
        pageBreakAfter: "always",
        pageBreakInside: "avoid",
        maxWidth: 480,
        mx: "auto",
        "@media print": {
          border: "2px solid #000",
          borderRadius: 0,
          mb: 0,
          p: "12mm",
          maxWidth: "none",
          width: "100%",
        },
      }}
    >
      {/* Reference — large, prominent */}
      <Typography
        variant="h4"
        fontWeight={800}
        sx={{
          textAlign: "center",
          letterSpacing: 1,
          mb: 1.5,
          fontFamily: "monospace",
          fontSize: { xs: 28, print: 32 },
          "@media print": { fontSize: "28pt" },
        }}
      >
        {label.reference}
      </Typography>

      <Divider sx={{ borderColor: "#000", borderWidth: 1, mb: 1.5 }} />

      {/* Client name */}
      {clientName && (
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            mb: 0.5,
            "@media print": { fontSize: "16pt" },
          }}
        >
          {clientName}
        </Typography>
      )}

      {/* Organisation */}
      {label.client?.organisation && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 0.5,
            "@media print": { fontSize: "11pt", color: "#444" },
          }}
        >
          {label.client.organisation}
        </Typography>
      )}

      {/* Delivery address */}
      {(addressLine1 || addressLine2) && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              display: "block",
              mb: 0.25,
              "@media print": { fontSize: "8pt" },
            }}
          >
            {t("print.deliveryAddress")}
          </Typography>
          {addressLine1WithApt && (
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ "@media print": { fontSize: "13pt" } }}
            >
              {addressLine1WithApt}
            </Typography>
          )}
          {addressLine2 && (
            <Typography
              variant="body1"
              sx={{ "@media print": { fontSize: "13pt" } }}
            >
              {addressLine2}
            </Typography>
          )}
          {label.address?.label && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ "@media print": { fontSize: "10pt", color: "#555" } }}
            >
              {label.address.label}
            </Typography>
          )}
        </Box>
      )}

      {/* Delivery instructions */}
      {label.address?.notes && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            p: 1,
            border: "1px dashed #666",
            borderRadius: 0.5,
            bgcolor: "#f9f9f9",
            "@media print": {
              bgcolor: "transparent",
              border: "1px dashed #000",
            },
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              display: "block",
              mb: 0.25,
              "@media print": { fontSize: "8pt" },
            }}
          >
            {t("print.deliveryInstructions")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              "@media print": { fontSize: "10pt" },
            }}
          >
            {label.address.notes}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Date + arrival time row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 0.5 }}
      >
        <Box>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              display: "block",
              "@media print": { fontSize: "8pt" },
            }}
          >
            {t("print.deliveryDate")}
          </Typography>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ "@media print": { fontSize: "12pt" } }}
          >
            {formattedDate}
          </Typography>
        </Box>
        {label.desiredArrivalTime && (
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.5,
                display: "block",
                "@media print": { fontSize: "8pt" },
              }}
            >
              {t("print.arrivalTime")}
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ "@media print": { fontSize: "12pt" } }}
            >
              {label.desiredArrivalTime.replace(/_/g, " ")}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Order notes */}
      {label.notes && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              display: "block",
              mb: 0.25,
              "@media print": { fontSize: "8pt" },
            }}
          >
            {t("print.notes")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              "@media print": { fontSize: "10pt" },
            }}
          >
            {label.notes}
          </Typography>
        </Box>
      )}

      {/* Tags */}
      {label.tags && label.tags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {label.tags.map((jt, i) => {
            const tagLabel = jt.tag?.label || `Tag #${jt.tagId}`
            return (
              <Box
                key={jt.tagId ?? i}
                sx={{
                  display: "inline-block",
                  px: 1,
                  py: 0.25,
                  border: "1px solid #333",
                  borderRadius: 0.5,
                  fontSize: 11,
                  fontWeight: 600,
                  "@media print": { fontSize: "9pt" },
                }}
              >
                {tagLabel}
              </Box>
            )
          })}
        </Stack>
      )}

      {/* Amount */}
      {label.amount != null && label.amount > 0 && (
        <Box sx={{ mt: 1, textAlign: "right" }}>
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ "@media print": { fontSize: "14pt" } }}
          >
            ${label.amount.toFixed(2)}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// PrintLabelView — renders labels and exposes a print trigger
// ---------------------------------------------------------------------------

export type PrintLabelViewHandle = {
  print: () => void
}

type PrintLabelViewProps = {
  labels: LabelData[]
}

export const PrintLabelView = forwardRef<PrintLabelViewHandle, PrintLabelViewProps>(
  ({ labels }, ref) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      print: () => {
        window.print()
      },
    }))

    if (labels.length === 0) return null

    return (
      <Box
        {...uiSurface("UI-DLV2-028")}
        ref={containerRef}
        className="print-label-container"
        sx={{
          // Hidden on screen, visible only when printing
          display: "none",
          "@media print": {
            display: "block",
          },
        }}
      >
        {labels.map((label, index) => (
          <SingleLabel key={`${label.reference}-${index}`} label={label} t={t} />
        ))}
      </Box>
    )
  },
)

PrintLabelView.displayName = "PrintLabelView"

// ---------------------------------------------------------------------------
// Print trigger helper — call from any component
// ---------------------------------------------------------------------------

/** Triggers window.print(). Companion to PrintLabelView (which hides/shows via @media print). */
export function triggerPrint(): void {
  window.print()
}
